import React, { useEffect, useMemo, useState } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import {
  Sparkles,
  Cpu,
  Workflow,
  LineChart,
  ShieldCheck,
  ArrowRight,
  ArrowUpRight,
  ArrowUp,
  Brain,
  Layers,
  Settings,
  CheckCircle2,
  Clock,
  Database,
  Lock,
  Wrench,
  ClipboardCheck,
  MessageSquareText,
  BarChart3,
} from 'lucide-react';

import * as assets from '../../assets';


/* ======================================================
   THEMES
====================================================== */

const theme = {
  bg: '#07070A',
  bgAlt: 'rgba(255,255,255,0.03)',
  card: 'rgba(255,255,255,0.06)',
  text: '#F5F5F7',
  subText: 'rgba(245,245,247,0.72)',
  accent: '#7C3AED', // purple
  border: 'rgba(255,255,255,0.12)',
};

const GlobalStyle = createGlobalStyle`
  :root { color-scheme: dark; }
  * { box-sizing: border-box; }
  html, body { height: 100%; }
  body {
    margin: 0;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji";
    background: ${theme.bg};
    color: ${theme.text};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  a { color: inherit; }
`;

/* ======================================================
   DATA
====================================================== */

const HERO_BADGES = [
  { icon: ClipboardCheck, label: 'Practical automation' },
  { icon: ShieldCheck, label: 'Audit-grade governance' },
  { icon: BarChart3, label: 'Decision-grade outputs' },
  { icon: LineChart, label: 'ASX-listed experience' },
];


const RESULTS = [
  {
    number: '75%',
    label: 'Reduction in accounting close time',
    detail: 'Reducing accounting close from Day 16 to Day 4',
  },
  {
    number: 'Real-time',
    label: 'Reporting & forecasting cadence',
    detail: 'Moving from quarterly reporting to near real-time visibility',
  },
  {
    number: 'Automate',
    label: 'Finance effort',
    detail: 'Replacing spreadsheet-driven work with exception-based workflows',
  },
  {
    number: '1-2 hours',
    label: 'Board pack preparation',
    detail: 'Reducing board pack preparation from 1–2 weeks to hours',
  },
];


const FEATURES = [
  {
    icon: Cpu,
    title: 'Consulting-led automation with AI where it helps',
    text: 'We work with your finance team to design and implement workflows using AI and automation where they deliver measurable value.',
  },
  {
    icon: Workflow,
    title: 'End-to-end workflow automation (designed & implemented with your team)',
    text: 'From ingestion → validation → reconciliation → reporting, workflows are orchestrated, monitored, versioned, and owned.',
  },
  {
    icon: LineChart,
    title: 'Decision-grade reporting',
    text: 'We optimise for accuracy, traceability, and narrative — board packs, investor reporting, and weekly performance rhythms.',
  },
  {
    icon: ShieldCheck,
    title: 'Built for scrutiny',
    text: 'Controls, logs, approvals, and documentation designed to hold up under audit, diligence, regulators, and ASX-level reporting scrutiny.',
  },
];

const DIFFERENTIATORS = [
  {
    icon: Layers,
    title: 'Finance leadership + technical execution',
    text: 'Optiii combines CFO-grade operating experience — including ASX-listed and investor-backed environments — with hands-on build capability.'
  },
  {
    icon: Settings,
    title: 'Automation that sticks',
    text: 'We define owners, governance, and operating rhythms so improvements survive month-end pressure and staff changes.',
  },
  {
    icon: Brain,
    title: 'Safe AI in production',
    text: 'We focus on bounded, useful AI: drafting, classification, variance narratives, retrieval, and controlled agent workflows.',
  },
];

const USE_CASES = [
  {
    icon: Database,
    title: 'Multi-system consolidation',
    text: 'Automate extraction, normalisation, and mapping across ERP, banking, billing, payroll, and ops systems.',
  },
  {
    icon: ClipboardCheck,
    title: 'Close & reconciliation uplift',
    text: 'Reduce manual journals, standardise reconciliations, and build exception-based reviews with clear audit trails.',
  },
  {
    icon: BarChart3,
    title: 'Board & investor reporting',
    text: 'Automated pack generation (tables + narrative), variance drivers, KPI definitions, and self-serve drill-down where it matters.',
  },
  {
    icon: MessageSquareText,
    title: 'AI-assisted analysis',
    text: 'CFO-grade narratives, variance commentary, Q&A prep, and structured insights grounded in your numbers (not generic fluff).',
  },
  {
    icon: Wrench,
    title: 'FP&A & scenario modelling',
    text: 'Repeatable models, scenario levers, forecast cadence, and alignment between finance, sales, and operations.',
  },
  {
    icon: Lock,
    title: 'Controls & governance',
    text: 'RBAC, approvals, change control, documentation, logging, and policy — so automation improves trust, not risk.',
  },
];

const OUTCOMES = [
  {
    title: 'Faster month-end close',
    bullets: ['Less manual rework', 'Exception-based reviews', 'Clear owners + checklists'],
  },
  {
    title: 'More reliable forecasting',
    bullets: ['Defined driver model', 'Weekly rhythm', 'Scenario levers and variance tracking'],
  },
  {
    title: 'Board-ready outputs',
    bullets: ['Consistent KPI definitions', 'Automated pack drafts', 'Traceable numbers and narratives'],
  },
  {
    title: 'A finance OS you can scale',
    bullets: ['Standardised workflows', 'Documentation + controls', 'Reduced key-person risk'],
  },
];

const PROCESS = [
  {
    icon: Clock,
    title: 'Weeks 1–2: Diagnostic & opportunity map',
    text: 'We map your decision needs, close process, reporting chain, data sources, controls, and pain points. We quantify impact, prioritise wins, and establish governance.',
    bullets: [
      'Current-state workflow + control mapping',
      'Data lineage + source-of-truth review',
      'Opportunity register (quick wins + structural fixes)',
      'Target operating rhythm (weekly/monthly/board)',
    ],
  },
  {
    icon: ClipboardCheck,
    title: 'Weeks 3–6: Blueprint & delivery plan',
    text: 'We design your future-state architecture, workflow ownership, tooling, and a clear delivery plan. No fluff: what you build, what you buy, and what you stop doing.',
    bullets: [
      'Future-state architecture and standards',
      'Governance: owners, approvals, logging',
      'Tooling selection and integration plan',
      'Implementation backlog + timeline',
    ],
  },
  {
    icon: Wrench,
    title: 'Weeks 7–14: Implementation support',
    text: 'We implement with you (or guide your team). We build automations, templates, controls, and train staff so the changes are embedded and measured.',
    bullets: [
      'Automation build + iteration',
      'Documentation, playbooks, handover',
      'Training and adoption support',
      'Measurement: cycle time, error rate, rework',
    ],
  },
];

const GOVERNANCE = [
  {
    title: 'Bounded AI',
    text: 'We apply AI where it reduces time without increasing risk: drafting narratives, categorising transactions, triaging exceptions, and summarising variance drivers.',
  },
  {
    title: 'Traceability',
    text: 'Every output links back to source data and assumptions. If you can’t defend it in a room, it doesn’t ship.',
  },
  {
    title: 'Controls-first',
    text: 'Approvals, access, audit logs, and change control so automation improves confidence, not anxiety.',
  },
];

/* ======================================================
   HELPERS
====================================================== */

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/* ======================================================
   PAGE
====================================================== */

const OptiiiPage = () => {
  const { scrollY } = useScroll();
  const showTop = useTransform(scrollY, [300, 520], [0, 1]);

  

  const currentYear = new Date().getFullYear();

  // Contact form

  const [status, setStatus] = useState({ state: 'idle', msg: '' }); // idle | sending | success | error

  const [submittedData, setSubmittedData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
  });

  useEffect(() => window.scrollTo(0, 0), []);

  const [state, handleSubmit] = useForm("mkoowyoa");



  const onSubmit = (e) => {
    e.preventDefault(); // CRITICAL

    if (window.gtag) {
      window.gtag_report_conversion();
    }

    handleSubmit(e);
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />

      <Page>
        {/* ===== HERO ===== */}
        <Hero>
          <HeroBg />

          <Nav>
            <NavInner>
              <BrandRow onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <img style={{width:'17px'}} src={assets.LogoOptiiiIconOnly} alt={`Icon`} />
                <BrandName>Optiii</BrandName>
              </BrandRow>

              <NavLinks>
                <NavLink onClick={() => scrollToId('capabilities')}>Capabilities</NavLink>
                <NavLink onClick={() => scrollToId('use-cases')}>Use cases</NavLink>
                <NavLink onClick={() => scrollToId('process')}>Process</NavLink>
                <NavLink onClick={() => scrollToId('contact')}>Contact</NavLink>
              </NavLinks>

              <NavCTA onClick={() => scrollToId('contact')}>
                Start a conversation <ArrowUpRight size={16} />
              </NavCTA>
            </NavInner>
          </Nav>

          <HeroInner
            as={motion.div}
            initial={{ opacity: 0, y: 34 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Eyebrow>
              <Sparkles size={14} />
              Specialists in finance uplift & automation
            </Eyebrow>

            <HeroTitle>
              We help finance leaders build a finance function that scales with <em>accuracy</em>, <em>control</em>, and <em>leverage</em>
            </HeroTitle>

            <HeroSub>
              Optiii is a specialist finance consulting firm to uplift busy finance teams.
              We help your team reduce manual effort, improve decision cadence, and deliver audit-grade governance.
            </HeroSub>

            <TrustLine>
              Experience across <strong>ASX-listed</strong>, PE-backed, and founder-led organisations — from close and reporting through to board and investor scrutiny.
            </TrustLine>

            <HeroBadges>
              {HERO_BADGES.map((b) => {
                const Icon = b.icon;
                return (
                  <Badge key={b.label}>
                    <Icon size={16} />
                    {b.label}
                  </Badge>
                );
              })}
            </HeroBadges>

            <HeroActions>
              <PrimaryButton onClick={() => scrollToId('capabilities')}>
                Explore capabilities <ArrowRight size={18} />
              </PrimaryButton>
              <SecondaryButton onClick={() => scrollToId('process')}>
                See the engagement model <ArrowRight size={18} />
              </SecondaryButton>
            </HeroActions>

            <TrustLine>
              Designed for CFOs who want: <strong>less rework</strong>, <strong>faster close</strong>, <strong>clean data lineage</strong>, and{' '}
              <strong>board-ready reporting</strong>.
            </TrustLine>
          </HeroInner>
        </Hero>

        {/* ===== CAPABILITIES ===== */}
        <Section id="capabilities">
          <Inner>
            <SectionTitle>What Optiii delivers</SectionTitle>
            <SectionSub>
              Through structured consulting engagements, we design and embed the operating layer between your systems and your decisions.
            </SectionSub>

            <Grid>
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Card
                    as={motion.div}
                    key={f.title}
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <IconWrap>
                      <Icon size={22} />
                    </IconWrap>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </Card>
                );
              })}
            </Grid>

            <Divider />

            <TwoCol>
              <Col>
                <MiniTitle>How we avoid “AI theatre”</MiniTitle>
                <MiniCopy>
                  We only ship automation that is measurable, governed, and owned. If the output isn’t traceable back to source-of-truth data,
                  or it introduces uncontrolled risk, it doesn’t make the cut.
                </MiniCopy>

                <MiniList>
                  {GOVERNANCE.map((g) => (
                    <MiniItem key={g.title}>
                      <MiniItemHead>
                        <CheckCircle2 size={18} />
                        {g.title}
                      </MiniItemHead>
                      <MiniItemText>{g.text}</MiniItemText>
                    </MiniItem>
                  ))}
                </MiniList>
              </Col>

              <Col>
                <MiniTitle>Typical deliverables</MiniTitle>
                <MiniCopy>Concrete outputs your team can run with — not a slide deck that dies in a folder.</MiniCopy>

                <Deliverables>
                  <Deliverable>Close playbook + reconciliation standards</Deliverable>
                  <Deliverable>Data lineage map + source-of-truth definitions</Deliverable>
                  <Deliverable>Board pack templates (tables + narratives)</Deliverable>
                  <Deliverable>Automation workflows + monitoring + alerting</Deliverable>
                  <Deliverable>Controls: access, approvals, logging, change control</Deliverable>
                  <Deliverable>Operating rhythm: weekly performance & forecast cadence</Deliverable>
                </Deliverables>
              </Col>
            </TwoCol>
          </Inner>
        </Section>

        {/* ===== DIFFERENTIATORS ===== */}
        <AltSection>
          <Inner>
            <SectionTitle>Why CFOs choose Optiii</SectionTitle>
            <SectionSub>
              Most “automation consulting” either lacks finance context or lacks build capability.
              Optiii is designed to deliver outcomes in real finance environments — close, reporting, controls, and execution.
            </SectionSub>

            <Grid>
              {DIFFERENTIATORS.map((e, i) => {
                const Icon = e.icon;
                return (
                  <Card
                    as={motion.div}
                    key={e.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <IconWrap>
                      <Icon size={22} />
                    </IconWrap>
                    <h3>{e.title}</h3>
                    <p>{e.text}</p>
                  </Card>
                );
              })}
            </Grid>

            <OutcomeRow>
              {OUTCOMES.map((o) => (
                <OutcomeCard key={o.title}>
                  <OutcomeTitle>{o.title}</OutcomeTitle>
                  <OutcomeList>
                    {o.bullets.map((b) => (
                      <OutcomeItem key={b}>
                        <CheckCircle2 size={16} />
                        {b}
                      </OutcomeItem>
                    ))}
                  </OutcomeList>
                </OutcomeCard>
              ))}
            </OutcomeRow>
          </Inner>
        </AltSection>

       

        <Section>
          <Inner>
            <SectionTitle>Real results, measured in numbers</SectionTitle>
            <SectionSub>
              We focus on outcomes CFOs actually care about — cycle time, accuracy,
              confidence in the numbers, and decision velocity.
            </SectionSub>

            <ResultsGrid>
              {RESULTS.map(r => (
                <ResultCard key={r.label}>
                  {/* BIG NUMBER */}
                  <ResultNumber>{r.number}</ResultNumber>

                  {/* WHAT IT MEANS */}
                  <ResultLabel>{r.label}</ResultLabel>

                  {/* PROOF / CONTEXT */}
                  <ResultDetail>{r.detail}</ResultDetail>
                </ResultCard>
              ))}
            </ResultsGrid>
            <ResultNote>
              Results shown are representative of engagements across ASX-listed
              and founder-led organisations where finance automation was implemented with
              clear ownership, governance, and executive sponsorship.
            </ResultNote>
          </Inner>
        </Section>


        {/* ===== USE CASES ===== */}
        <Section id="use-cases">
          <Inner>
            <SectionTitle>High-leverage use cases</SectionTitle>
            <SectionSub>
              We focus on areas where automation materially changes capability — cycle time, accuracy, control, and decision cadence.
            </SectionSub>

            <UseCaseGrid>
              {USE_CASES.map((u, i) => {
                const Icon = u.icon;
                return (
                  <UseCaseCard
                    as={motion.div}
                    key={u.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <UseCaseHead>
                      <IconWrap>
                        <Icon size={20} />
                      </IconWrap>
                      <h3>{u.title}</h3>
                    </UseCaseHead>
                    <p>{u.text}</p>
                  </UseCaseCard>
                );
              })}
            </UseCaseGrid>

            <NoteBox>
              <NoteTitle>Tooling-agnostic (but opinionated)</NoteTitle>
              <NoteText>
                We work with the tools you already have where possible. Where change is required, we recommend the smallest set of systems
                that gives you reliable source-of-truth, clean workflows, and repeatable outputs.
              </NoteText>
            </NoteBox>
          </Inner>
        </Section>


        {/* ===== PROCESS ===== */}
        <AltSection id="process">
          <Inner>
            <SectionTitle>Engagement model</SectionTitle>
            <SectionSub>
              A standard Optiii engagement runs <strong>2–4 months</strong>: we diagnose fast, plan clearly, and implement with governance and adoption.
              Every engagement is a bespoke consulting engagement tailored to your finance team, systems, and operating context. You can implement with us, or we can support your team to self-implement.
            </SectionSub>

            <ProcessGrid>
              {PROCESS.map((p, idx) => {
                const Icon = p.icon;
                return (
                  <ProcessCard
                    as={motion.div}
                    key={p.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.08, duration: 0.55 }}
                    viewport={{ once: true }}
                  >
                    <ProcessTop>
                      <IconWrap>
                        <Icon size={22} />
                      </IconWrap>
                      <div>
                        <ProcessTitle>{p.title}</ProcessTitle>
                        <ProcessText>{p.text}</ProcessText>
                      </div>
                    </ProcessTop>

                    <ProcessBullets>
                      {p.bullets.map((b) => (
                        <ProcessBullet key={b}>
                          <CheckCircle2 size={16} />
                          {b}
                        </ProcessBullet>
                      ))}
                    </ProcessBullets>
                  </ProcessCard>
                );
              })}
            </ProcessGrid>

            <ProcessFooter>
              <ProcessFooterCard>
                <MiniTitle>What success looks like</MiniTitle>
                <MiniCopy>
                  Finance runs with a consistent cadence, your numbers are defensible, reporting is faster and cleaner, and your team spends more time on decisions — not data wrangling.
                </MiniCopy>
              </ProcessFooterCard>

              <ProcessFooterCard>
                <MiniTitle>How we measure impact</MiniTitle>
                <MiniListTight>
                  <li>Close cycle time + rework reduction</li>
                  <li>Reconciliation exceptions (count + severity)</li>
                  <li>Forecast accuracy + variance explainability</li>
                  <li>Board pack production time</li>
                  <li>Key-person risk reduction (documentation + standardisation)</li>
                </MiniListTight>
              </ProcessFooterCard>
            </ProcessFooter>
          </Inner>
        </AltSection>

        {/* ===== CONTACT CTA ===== */}
        <CTA id="contact">
          <Inner>
            <ContactGrid>
              {/* LEFT CONTENT */}
              <Left
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55 }}
                viewport={{ once: true }}
              >
                <Title>Ready to uplift your finance function?</Title>

                <Copy>
                  If you’re a CFO (or finance leader) who wants practical automation — faster close, cleaner reporting, stronger controls, and bounded AI that actually helps — Optiii is built for you.
                </Copy>

                <ContactPoints>
                  <Point>
                    <CheckCircle2 size={18} />
                    Standard engagement: 2–4 months
                  </Point>
                  <Point>
                    <CheckCircle2 size={18} />
                    Diagnostic → Blueprint → Implementation support
                  </Point>
                  <Point>
                    <CheckCircle2 size={18} />
                    Designed to stand up under audit / diligence
                  </Point>
                </ContactPoints>

                <EmailLink href="mailto:team@optiii.com">
                  team@optiii.com <ArrowUpRight size={18} />
                </EmailLink>
              </Left>

              {/* RIGHT FORM */}
              <FormCard
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.08 }}
                viewport={{ once: true }}
              >
                {!state.succeeded ? (
                  <>
                    <FormTitle>
                      Start a no-commitment discussion about uplifting your finance function
                    </FormTitle>

                    <form onSubmit={onSubmit}>
                      <input type="hidden" name="_subject" value="Optiii - Finance Solutions Inquiry" />
                      <input type="text" name="_gotcha" style={{ display: 'none' }} />


                      <Field>
                        <label>Your name</label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Your full name"
                          onChange={(e) =>
                            setSubmittedData(prev => ({ ...prev, name: e.target.value }))
                          }
                        />
                        <ValidationError prefix="Name" field="name" errors={state.errors} />
                      </Field>

                      <Field>
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="you@company.com"
                          onChange={(e) =>
                            setSubmittedData(prev => ({ ...prev, email: e.target.value }))
                          }
                        />
                        <ValidationError prefix="Email" field="email" errors={state.errors} />
                      </Field>

                      <Field>
                        <label>Phone (optional)</label>
                        <input
                          type="phone"
                          name="phone"
                          required={false}
                          placeholder="+61 423 543 323"
                          onChange={(e) =>
                            setSubmittedData(prev => ({ ...prev, phone: e.target.value }))
                          }
                        />
                        <ValidationError prefix="Phone" field="phone" errors={state.errors} />
                      </Field>

                      <Field>
                        <label>Company</label>
                        <input
                          type="text"
                          name="company"
                          placeholder="Your Company Pty Ltd"
                          onChange={(e) =>
                            setSubmittedData(prev => ({ ...prev, company: e.target.value }))
                          }
                        />
                        <ValidationError prefix="Company" field="company" errors={state.errors} />
                      </Field>

                      <Field>
                        <label>What are you trying to improve?</label>
                        <textarea
                          name="message"
                          rows={4}
                          placeholder="Close process, reporting, FP&A, automation…"
                        />
                        <ValidationError prefix="Message" field="message" errors={state.errors} />
                      </Field>

                      <SubmitButton
                          type="submit"
                          disabled={state.submitting}
                        >
                          {state.submitting ? 'Sending…' : `I'm interested to understand more`}
                        </SubmitButton>
                      </form>
                    </>
                  ) : (
                    <SuccessWrap
                      as={motion.div}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <SuccessTitle>
                        Thanks{submittedData.name ? `, ${submittedData.name}` : ''}.
                      </SuccessTitle>

                      <SuccessText>
                        We'll be in touch shortly to arrange a <strong>confidential, obligation-free</strong> discussion on how Optiii can help{' '}
                        <strong>
                          {submittedData.company || 'your finance team'}
                        </strong>{' '}
                        strengthen automation, insight and execution across the finance function.
                      </SuccessText>

                      <SuccessSub>
                        We typically respond within <strong>24 hours</strong>.
                      </SuccessSub>
                    </SuccessWrap>
                  )}
                </FormCard>

            </ContactGrid>
          </Inner>
        </CTA>


         {/* ======================================================
          TRANSACTION & CORPORATE ADVISORY HANDOFF
        ====================================================== */}
        <AltSection>
          <Inner>
            <Header>
              <Eyebrow>Looking ahead</Eyebrow>
              <SectionTitle>
                When finance readiness turns into a transaction
              </SectionTitle>
              <SectionSub>
                Many Optiii engagements are driven by an upcoming inflection point —
                a business sale, capital raise, shareholder restructure, or board-level decision.
                <br /><br />
                When specialist corporate advice is required, we partner with{' '}
                <strong>SME Cap Advisory</strong> to ensure execution quality matches
                the strength of the underlying numbers.
              </SectionSub>
            </Header>

            <HandoffBridge>
              {/* LEFT */}
              <HandoffLeft>
                <HandoffTitle>Why the partnership works</HandoffTitle>
                <HandoffText>
                  Optiii prepares finance teams for scrutiny —
                  clean numbers, defensible reporting, and audit-grade controls.
                  <br /><br />
                  SME Cap Advisory leads the transaction process itself:
                  positioning, valuation, buyer engagement, negotiation,
                  and execution — principal-led from start to finish.
                  <br /><br />
                  The result is continuity from preparation through to outcome,
                  without losing context, momentum, or credibility.
                </HandoffText>

                <HandoffCallout>
                  Optiii makes the numbers stand up and develops finance capability into your team.
                  SME Cap Advisory ensures value is realised.
                </HandoffCallout>
              </HandoffLeft>

              {/* RIGHT */}
              <HandoffRight>
                <PartnerCard>
                  
                  <PartnerLogo>
                    <Logo src={assets.LogoFull} />
                  </PartnerLogo>
                  <PartnerTagline>
                    Boutique corporate advisory for SME founders, owners and boards
                  </PartnerTagline>

                  <PartnerList>
                    <li>Business sales & M&A</li>
                    <li>Valuations & value positioning</li>
                    <li>Capital strategy & shareholder advice</li>
                    <li>Principal-led, success-aligned engagements</li>
                  </PartnerList>

                  <PartnerCTA
                    href="https://smecapadvisory.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Explore SME Cap Advisory →
                  </PartnerCTA>
                </PartnerCard>
              </HandoffRight>
            </HandoffBridge>
          </Inner>
        </AltSection>

        {/* ===== FOOTER ===== */}
        <FooterWrap>
          <TopDivider />

          <FooterInner>
            {/* LEFT */}
            <Brand>
              
              <BrandText>
                Optiii is a specialist finance consulting firm helping modern finance teams —
                including ASX-listed and investor-backed organisations — design and implement
                practical automation and bounded AI — improving close, reporting, controls, and decision cadence.
                <br /><br />Optiii does not sell specific subscriptions or software. All work is delivered through 
                senior-led consulting engagements.
              </BrandText>

              <Footnote>
                Optiii does not sell specific subscriptions or software. All work is delivered through senior-led consulting engagements.
                <br /><br />
                Optiii provides management consulting and implementation services only.
                Optiii does not provide financial product advice, deal in financial products,
                or hold an Australian Financial Services Licence (AFSL). 
              </Footnote>
            </Brand>

            {/* RIGHT */}
            <Links>
              <Column>
                <ColumnTitle>Focus areas</ColumnTitle>
                <a href="#capabilities" onClick={(e) => (e.preventDefault(), scrollToId('capabilities'))}>Capabilities</a>
                <a href="#use-cases" onClick={(e) => (e.preventDefault(), scrollToId('use-cases'))}>Use cases</a>
                <a href="#process" onClick={(e) => (e.preventDefault(), scrollToId('process'))}>Engagement model</a>
                <a href="#contact" onClick={(e) => (e.preventDefault(), scrollToId('contact'))}>Contact</a>
              </Column>

              <Column>
                <ColumnTitle>Contact</ColumnTitle>
                <a href="mailto:team@optiii.com">team@optiii.com</a>
                <a href="#contact" onClick={(e) => (e.preventDefault(), scrollToId('contact'))}>Request a consultation</a>
                <a href="#process" onClick={(e) => (e.preventDefault(), scrollToId('process'))}>How we work</a>
                <a href="/privacy">Privacy policy</a>
                <a href="/terms">Terms of service</a>
              </Column>
            </Links>
          </FooterInner>

          <BottomDivider />

          <BottomRow>
            <CopySmall>© {currentYear} Optiii. All rights reserved.</CopySmall>
            <Location>Based in Australia • Serving teams globally</Location>
          </BottomRow>
        </FooterWrap>

        {/* ===== BACK TO TOP ===== */}
        <TopButton
          as={motion.button}
          style={{ opacity: showTop }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
        >
          <ArrowUp />
        </TopButton>
      </Page>
    </ThemeProvider>
  );
};

export default OptiiiPage;

/* ======================================================
   STYLES
====================================================== */

const Page = styled.div``;

/* --- Nav --- */
const Nav = styled.div`
  position: fixed;
  top: 16px;
  left: 0;
  right: 0;
  z-index: 80;
  padding: 0 18px;
`;

const NavInner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(14px);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const BrandRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 999px;

  &:hover {
    background: rgba(255,255,255,0.06);
  }
`;

const BrandMark = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: ${({ theme }) => `${theme.accent}22`};
  color: ${({ theme }) => theme.accent};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BrandName = styled.div`
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;

  @media (max-width: 860px) {
    display: none;
  }
`;

const NavLink = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.subText};
  cursor: pointer;
  font-size: 14px;
  padding: 8px 10px;
  border-radius: 999px;

  &:hover {
    color: ${({ theme }) => theme.text};
    background: rgba(255,255,255,0.06);
  }
`;

const NavCTA = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 10px 14px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) => theme.accent};
  color: #fff;
  font-weight: 700;

  @media (max-width: 860px) {
    padding: 10px 12px;
    font-size: 14px;
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px;
`;

/* --- Handoff section --- */

const Header = styled.div`
  max-width: 900px;
  margin-bottom: 48px;
`;

const HandoffBridge = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const HandoffLeft = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 22px;
  padding: 28px;
`;

const HandoffTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 14px;
`;

const HandoffText = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.subText};
  margin: 0 0 22px;
`;

const HandoffCallout = styled.div`
  background: rgba(255,255,255,0.06);
  border-left: 4px solid ${({ theme }) => theme.accent};
  padding: 16px 18px;
  border-radius: 14px;
  font-size: 15px;
  line-height: 1.6;

  strong {
    font-weight: 800;
  }
`;

const HandoffRight = styled.div`
  display: flex;
`;

const PartnerCard = styled.div`
  width: 100%;
  background: #ffffff;
  color: #111827;
  border-radius: 22px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const PartnerLogo = styled.div`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.2px;
  margin-bottom: 6px;
`;

const PartnerTagline = styled.div`
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 18px;
`;

const PartnerList = styled.ul`
  padding-left: 18px;
  margin: 0 0 22px;
  display: grid;
  gap: 8px;
  font-size: 15px;
  color: #111827;
`;

const PartnerCTA = styled.a`
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 15px;
  color: #000;
  text-decoration: none;
  border-bottom: 2px solid rgba(0,0,0,0.15);
  width: fit-content;

  &:hover {
    border-color: #000;
  }
`;


const ResultCard = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 22px;
  padding: 24px;
`;

const SuccessWrap = styled.div`
  min-height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

const SuccessTitle = styled.h3`
  font-size: 28px;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
`;

const SuccessText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: ${({ theme }) => theme.subText};

  strong {
    color: ${({ theme }) => theme.text};
    font-weight: 500;
  }
`;

const SuccessSub = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.subText};
`;


const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;


const ResultValue = styled.div`
  font-size: 16px;
  font-weight: 700;

  ${({ muted }) =>
    muted &&
    `
    color: rgba(245,245,247,0.55);
  `}

  ${({ highlight, theme }) =>
    highlight &&
    `
    color: ${theme.accent};
  `}
`;

const ResultImpact = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(245,245,247,0.9);
`;

const ResultNote = styled.p`
  margin-top: 28px;
  font-size: 14px;
  color: ${({ theme }) => theme.subText};
  max-width: 760px;
`;


/* --- Hero --- */
const Hero = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 110px 24px 60px;
  position: relative;
  overflow: hidden;
`;

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(800px 360px at 18% 30%, ${({ theme }) => `${theme.accent}28`}, transparent 55%),
    radial-gradient(800px 360px at 78% 70%, rgba(59,130,246,0.18), transparent 55%),
    linear-gradient(180deg, rgba(255,255,255,0.03), transparent 28%);
  pointer-events: none;
`;

const ResultHeadline = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${({ theme }) => theme.accent};
  margin-bottom: 8px;
  line-height: 1.2;
`;

const ResultMetric = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const ResultContext = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.subText};
  line-height: 1.6;
  margin: 0;

  strong {
    color: rgba(245,245,247,0.9);
    font-weight: 600;
  }
`;

const HeroInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
  position: relative;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1.1px;
  color: ${({ theme }) => theme.subText};
  margin-bottom: 18px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(35px, 6vw, 64px);
  font-weight: 520;
  line-height: 1.04;
  margin: 0;

  em {
    font-style: italic;
    font-weight: 650;
  }
`;

const HeroSub = styled.p`
  font-size: 20px;
  max-width: 760px;
  margin: 22px 0 26px;
  color: ${({ theme }) => theme.subText};
  line-height: 1.6;
`;

const HeroBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 26px;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(255,255,255,0.06);
  border: 1px solid ${({ theme }) => theme.border};
  color: rgba(245,245,247,0.9);
  font-size: 14px;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.accent};
  color: #fff;
  border: none;
  padding: 15px 20px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
`;

const SecondaryButton = styled.button`
  background: rgba(255,255,255,0.06);
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => theme.border};
  padding: 15px 20px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 650;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,0.09);
  }
`;

const TrustLine = styled.div`
  margin-top: 18px;
  color: ${({ theme }) => theme.subText};
  font-size: 14px;
  line-height: 1.6;

  strong { color: rgba(245,245,247,0.92); }
`;

/* --- Sections --- */
const Section = styled.section`
  padding: 110px 24px;
`;

const AltSection = styled(Section)`
  background: ${({ theme }) => theme.bgAlt};
`;

const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 38px;
  font-weight: 520;
  margin: 0 0 18px;
  width:100%;
`;

const SectionSub = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.subText};
  max-width: 900px;
  margin: 0 0 46px;
  line-height: 1.7;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 22px;
  padding: 26px;
  overflow: hidden;

  h3 {
    margin: 18px 0 10px;
    font-size: 18px;
  }

  p {
    color: ${({ theme }) => theme.subText};
    line-height: 1.65;
    margin: 0;
    font-size: 15px;
  }
`;

const IconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: ${({ theme }) => `${theme.accent}22`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.accent};
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.border};
  margin: 46px 0;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Col = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 22px;
  padding: 26px;
`;

const MiniTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 18px;
`;

const MiniCopy = styled.p`
  margin: 0 0 16px;
  color: ${({ theme }) => theme.subText};
  line-height: 1.7;
`;

const MiniList = styled.div`
  display: grid;
  gap: 14px;
`;

const MiniItem = styled.div``;

const MiniItemHead = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 750;
`;

const MiniItemText = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.subText};
  line-height: 1.7;
`;

const Deliverables = styled.div`
  display: grid;
  gap: 10px;
`;

const Deliverable = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
  padding: 12px 14px;
  color: rgba(245,245,247,0.9);
`;

/* --- Outcomes --- */
const OutcomeRow = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const OutcomeCard = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 22px;
  padding: 18px;
`;

const OutcomeTitle = styled.div`
  font-weight: 800;
  margin-bottom: 12px;
`;

const OutcomeList = styled.div`
  display: grid;
  gap: 10px;
`;

const OutcomeItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: ${({ theme }) => theme.subText};

  svg { margin-top: 2px; }
`;

/* --- Use cases --- */
const UseCaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
`;

const UseCaseCard = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 22px;
  padding: 22px;

  p {
    color: ${({ theme }) => theme.subText};
    line-height: 1.7;
    margin: 10px 0 0;
    font-size: 15px;
  }
`;

const UseCaseHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  h3 {
    margin: 0;
    font-size: 18px;
  }
`;

const NoteBox = styled.div`
  margin-top: 22px;
  border-radius: 22px;
  border: 1px solid ${({ theme }) => theme.border};
  background: rgba(255,255,255,0.03);
  padding: 20px 22px;
`;

const NoteTitle = styled.div`
  font-weight: 850;
  margin-bottom: 8px;
`;

const NoteText = styled.div`
  color: ${({ theme }) => theme.subText};
  line-height: 1.7;
`;

/* --- Process --- */
const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;
`;

const ProcessCard = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 22px;
  padding: 22px;
`;

const ProcessTop = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const ProcessTitle = styled.div`
  font-weight: 850;
  font-size: 18px;
  margin-bottom: 8px;
`;

const ProcessText = styled.div`
  color: ${({ theme }) => theme.subText};
  line-height: 1.7;
`;

const ProcessBullets = styled.div`
  margin-top: 14px;
  display: grid;
  gap: 10px;
`;

const ProcessBullet = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: rgba(245,245,247,0.86);

  svg {
    margin-top: 2px;
    color: ${({ theme }) => theme.accent};
  }
`;

const ProcessFooter = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessFooterCard = styled.div`
  background: rgba(0,0,0,0.35);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 22px;
  padding: 22px;
`;

const MiniListTight = styled.ul`
  margin: 10px 0 0;
  padding-left: 18px;
  color: ${({ theme }) => theme.subText};
  line-height: 1.7;
`;

/* --- CTA / Contact --- */
const CTA = styled.section`
  padding: 120px 24px;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Left = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 28px;
  padding: 34px;
`;

const Title = styled.h2`
  font-size: 40px;
  font-weight: 650;
  margin: 0 0 12px;

  @media (max-width: 560px) {
    font-size: 34px;
  }
`;

const Copy = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: ${({ theme }) => theme.subText};
  margin: 0 0 18px;
`;

const ContactPoints = styled.div`
  display: grid;
  gap: 10px;
  margin: 0 0 22px;
`;

const Point = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: rgba(245,245,247,0.86);

  svg { margin-top: 2px; color: ${({ theme }) => theme.accent}; }
`;

const EmailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  color: #fff;
  text-decoration: none;
  border-bottom: 1px solid rgba(255,255,255,0.35);
  padding-bottom: 4px;
  width: fit-content;

  &:hover {
    border-color: #fff;
  }
`;

const FormCard = styled.div`
  background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05));
  border: 1px solid ${({ theme }) => theme.border};
  backdrop-filter: blur(12px);
  border-radius: 28px;
  padding: 34px;
  box-shadow: 0 40px 80px rgba(0,0,0,0.45);

  @media (max-width: 900px) {
    padding: 28px 22px;
  }
`;

const FormTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  margin: 0 0 18px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;

  label {
    font-size: 13px;
    color: rgba(245,245,247,0.72);
    margin-bottom: 6px;
  }

  input, textarea {
    background: rgba(0,0,0,0.25);
    border: 1px solid rgba(255,255,255,0.16);
    border-radius: 14px;
    padding: 12px 14px;
    color: #fff;
    font-size: 15px;
    line-height: 1.5;

    &::placeholder { color: rgba(245,245,247,0.45); }

    &:focus {
      outline: none;
      border-color: rgba(255,255,255,0.32);
    }
  }

  textarea { resize: vertical; min-height: 110px; }
`;

const SubmitButton = styled.button`
  margin-top: 8px;
  background: #fff;
  color: #000;
  border: none;
  border-radius: 999px;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 850;
  cursor: pointer;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const FormNotice = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ $ok }) => ($ok ? 'rgba(167,243,208,0.95)' : 'rgba(252,165,165,0.95)')};
`;

const Footnote = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: rgba(245,245,247,0.62);
  line-height: 1.6;
`;

/* --- Footer --- */
const FooterWrap = styled.footer`
  background: #000;
  color: rgba(245,245,247,0.72);
  padding: 0 24px 34px;
`;

const TopDivider = styled.div`
  height: 1px;
  background: rgba(255,255,255,0.12);
  margin-bottom: 34px;
`;

const FooterInner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  gap: 40px;
  align-items: flex-start;

  /* THIS is the key fix so the right column doesn't drop under */
  flex-wrap: wrap;

  @media (max-width: 980px) {
    flex-direction: column;
    gap: 26px;
  }
`;

const Brand = styled.div`
  flex: 1;
  min-width: 280px;
  max-width: 520px;
`;

const LogoWrap = styled.div`
  cursor: pointer;
  width: fit-content;
`;

const Logo = styled.img`
  height: 44px;
  object-fit: contain;
`;

const BrandText = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: rgba(245,245,247,0.62);
  margin: 14px 0 0;
`;

const Links = styled.div`
  flex: 1;
  min-width: 320px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 60px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  a {
    color: rgba(245,245,247,0.62);
    text-decoration: none;
    font-size: 16px;
    transition: color 0.2s ease;

    &:hover { color: #fff; }
  }
`;

const ColumnTitle = styled.h4`
  font-size: 16px;
  font-weight: 850;
  color: #fff;
  margin: 0 0 8px;
`;

const BottomDivider = styled.div`
  height: 1px;
  background: rgba(255,255,255,0.12);
  margin: 34px 0 18px;
`;

const BottomRow = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  gap: 12px;

  @media (max-width: 700px) {
    flex-direction: column;
    text-align: center;
  }
`;

const CopySmall = styled.div`
  color: rgba(245,245,247,0.62);
`;

const Location = styled.div`
  color: rgba(245,245,247,0.52);
`;

/* --- Back to top --- */
const TopButton = styled.button`
  position: fixed;
  bottom: 22px;
  right: 22px;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.16);
  background: rgba(0,0,0,0.75);
  color: #fff;
  cursor: pointer;
  z-index: 90;
`;


const ResultNumber = styled.div`
  font-size: 44px;
  font-weight: 900;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.accent};
  margin-bottom: 6px;
`;

const ResultLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.text};
`;

const ResultDetail = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.subText};
  margin: 0;
`;